import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bookmark, Bell, Trash2, CheckCheck, SearchX } from "lucide-react";
import { toast } from "sonner";

import { normalizeSaved, normalizeNotifications } from "@/lib/coreShapes";
import { text, isActivityCancelled, participationState } from "@/lib/memberDerive";
import { filterSaved, noticeContext, noticeTime, firstNotice } from "@/lib/v3Plans";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { runAction } from "../actions";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, EmptyState, Unavailable, ErrorNote } from "../components/kit";
import { V3Title, V3Actions, V3_PRIMARY } from "../components/v3";
import { SearchField, FilterBar, Chip, Pill, ParticipationPill, ResultTable, ResultRow, V3_GHOST, V3_LINK } from "../components/v3app";
import { ActivityListCard } from "../components/v3act";

function CollectionFormatNote({ testid, onRetry }) {
  return <>
    <Unavailable id={`${testid}-reason`} testid={testid}>Die Antwort des Servers hat ein noch nicht abgestimmtes Format.</Unavailable>
    <button type="button" onClick={onRetry} aria-describedby={`${testid}-reason`} data-testid={`${testid}-retry`} className={`${V3_GHOST} mt-[12px]`}>Erneut laden</button>
  </>;
}

function SavedPill({ a }) {
  if (isActivityCancelled(a)) return <Pill tone="red" testid={`saved-item-${a.id}-cancelled`}>Aktivität abgesagt</Pill>;
  if (["confirmed", "registered", "requested", "offered"].includes(participationState(a))) return <ParticipationPill a={a} testid={`saved-item-${a.id}-status`} />;
  return <Pill testid={`saved-item-${a.id}-status`}>Nur gemerkt</Pill>;
}

// M22: saved activities from the Core; removing can be undone; saving is no participation. Search and chips only narrow the full list.
export function Saved() {
  const res = useCore("/member/saved-activities", (d) => ({ list: normalizeSaved(d) }));
  const { member } = useMemberAuth();
  const city = typeof member?.city === "string" ? member.city.trim() : "";
  const [q, setQ] = useState("");
  const [byCity, setByCity] = useState(false);
  const [access, setAccess] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const restore = async (id) => {
    const err = await runAction("post", `/member/saved-activities/${encodeURIComponent(id)}`, "Das Wiederherstellen wurde nicht gespeichert");
    if (err) { setError(err); toast.error(err); } else { toast.success("Wieder gemerkt."); res.reload(); }
  };
  const remove = async (a) => {
    setError(""); setBusy(a.id);
    const err = await runAction("delete", `/member/saved-activities/${encodeURIComponent(a.id)}`, "Das Entfernen wurde nicht gespeichert");
    setBusy("");
    if (err) { setError(err); return; }
    res.reload();
    toast.success(`„${text(a.title) || "Aktivität"}“ entfernt.`, { action: { label: "Rückgängig", onClick: () => restore(a.id) } });
  };
  const list = res.data?.list;
  const shown = list ? filterSaved(list, { q, city: byCity ? city : "", access }) : [];
  const reset = () => { setQ(""); setByCity(false); setAccess(false); };
  return (
    <div data-m="M22" data-testid="saved-page">
      <V3Title title="Merkliste und gespeicherte Ideen" intro="Deine gespeicherten Ideen sind noch keine Teilnahmezusagen." testid="saved-title" />
      <SearchField id="saved-search" value={q} onChange={setQ} placeholder="Aktivität oder öffentlichen Ort suchen …" label="Merkliste durchsuchen" testid="saved-search" disabled={!list} />
      <FilterBar testid="saved-filters">
        {city && <Chip pressed={byCity} onClick={() => setByCity((v) => !v)} testid="saved-filter-city">{city}</Chip>}
        <Chip pressed={access} onClick={() => setAccess((v) => !v)} testid="saved-filter-access">Zugangsangaben</Chip>
      </FilterBar>
      {error && <div className="mt-[12px]"><ErrorNote testid="saved-error">{error}</ErrorNote></div>}
      <div className="mt-[16px]">
        {res.error === "notfound" ? <Unavailable testid="saved-unavailable">Die Merkliste ist für dein Konto noch nicht abrufbar. Es wird nichts angezeigt und nichts gespeichert.</Unavailable>
          : res.error ? <ConnectionError onRetry={res.reload} /> : !res.data ? <Loading />
          : list === null ? <CollectionFormatNote testid="saved-format-note" onRetry={res.reload} />
          : list.length === 0 ? <EmptyState icon={Bookmark} title="Noch nichts gemerkt" text="Bei jeder Aktivität kannst du „Merken“ wählen." testid="saved-empty"><Link to="/aktivitaeten" className={V3_LINK}>Aktivitäten entdecken</Link></EmptyState>
          : shown.length === 0 ? <EmptyState icon={SearchX} title="Keine gemerkte Idee passt" text="Ändere den Suchbegriff oder die Auswahl." testid="saved-no-match"><button type="button" onClick={reset} data-testid="saved-reset" className={V3_LINK}>Suche zurücksetzen</button></EmptyState>
          : (<ul className="grid gap-[21px] lg:grid-cols-2" data-testid="saved-list" aria-label="Gemerkte Aktivitäten">{shown.map((a) => (
              <li key={a.id} data-testid={`saved-item-${a.id}`}>
                <ActivityListCard a={a} status={<SavedPill a={a} />}>
                  <button type="button" onClick={() => remove(a)} disabled={busy === a.id} data-testid={`saved-remove-${a.id}`} className={`${V3_GHOST} mt-[6px] w-full justify-center`}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" />{busy === a.id ? "Wird entfernt …" : "Nicht mehr merken"}<span className="sr-only">: {text(a.title) || "Aktivität"}</span>
                  </button>
                </ActivityListCard>
              </li>))}</ul>)}
      </div>
    </div>
  );
}

function NoticeDetail({ n, onRead, busy }) {
  return (
    <div className="mt-[6px] rounded-[10px] bg-v3-lilac px-[16px] py-[12px]" data-testid={`notification-${n.id}-detail`}>
      {n.body && <p className="break-words text-[14.5px] leading-[21px] text-v3-ink">{n.body}</p>}
      <p className="mt-[4px] text-[13px] text-v3-muted">{[noticeTime(n.createdAt), n.read ? "Gelesen" : "Ungelesen", "Kein weiteres Ziel in der App"].filter(Boolean).join(" · ")}</p>
      {!n.read && <button type="button" onClick={() => onRead(n)} disabled={busy} data-testid={`notification-read-${n.id}`} className={`${V3_GHOST} mt-[4px] -ml-[14px]`}><CheckCheck className="h-4 w-4" aria-hidden="true" />Als gelesen markieren</button>}
    </div>
  );
}

const NewPill = ({ testid }) => <span data-testid={testid} className="ml-[8px] inline-flex min-h-[20px] items-center rounded-full bg-v3-magenta px-[8px] align-middle text-[11.5px] font-bold text-white">Neu</span>;

// M23: a row opens its notification; with a target it is marked read and the target loads from the Core (rights re-checked),
// without one the row expands in place. "Hinweis öffnen" opens the newest unread notification.
export function Notifications() {
  const res = useCore("/member/notifications", (d) => ({ list: normalizeNotifications(d) }));
  const navigate = useNavigate();
  const [openId, setOpenId] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const markRead = async (n) => {
    setError(""); setBusy(true);
    const err = await runAction("post", `/member/notifications/${encodeURIComponent(n.id)}/read`, "Der Status wurde nicht gespeichert");
    setBusy(false);
    if (err) setError(err); else res.reload();
    return err;
  };
  // The target opens even if the read status failed; the failure stays visible as a toast after leaving.
  const open = async (n) => { const err = n.read ? "" : await markRead(n); if (err) toast.error(err); if (n.link) navigate(n.link); };
  const show = (n) => (n.link ? open(n) : setOpenId((v) => (v === n.id ? "" : n.id)));
  const list = res.data?.list;
  const next = list ? firstNotice(list) : null;
  return (
    <div data-m="M23" data-testid="notifications-page">
      <V3Title title="Benachrichtigungen" intro="Öffne den Hinweis, um den aktuellen Stand des Vorgangs anzusehen." testid="notifications-title" />
      {error && <div className="mb-[12px]"><ErrorNote testid="notifications-error">{error}</ErrorNote></div>}
      {res.error === "notfound" ? <Unavailable testid="notifications-unavailable">Benachrichtigungen sind für dein Konto noch nicht abrufbar.</Unavailable>
        : res.error ? <ConnectionError onRetry={res.reload} /> : !res.data ? <Loading />
        : list === null ? <CollectionFormatNote testid="notifications-format-note" onRetry={res.reload} />
        : list.length === 0 ? <EmptyState icon={Bell} title="Keine Benachrichtigungen" text="Neuigkeiten zu deinen Aktivitäten, Kreisen und Kontakten erscheinen hier." testid="notifications-empty" />
        : (<>
          <ResultTable testid="notifications-list" label="Benachrichtigungen">
            {list.map((n) => (
              <ResultRow key={n.id} initial="V" title={<>{n.title}{!n.read && <NewPill testid={`notification-${n.id}-new`} />}<span className="sr-only">{n.read ? " (gelesen)" : " (ungelesen)"}</span></>}
                context={noticeContext(n)} onOpen={() => show(n)} expanded={n.link ? undefined : openId === n.id} chevron={!n.link} testid={`notification-${n.id}`}>
                {!n.link && openId === n.id && <NoticeDetail n={n} onRead={markRead} busy={busy} />}
              </ResultRow>
            ))}
          </ResultTable>
          <V3Actions>
            <button type="button" onClick={() => show(next)} disabled={busy} data-testid="notifications-open" className={V3_PRIMARY}>
              Hinweis öffnen<span className="sr-only">: {next.title}</span>
            </button>
          </V3Actions>
        </>)}
      <p className="mt-[18px] text-[13px] leading-[19px] text-v3-muted" data-testid="notifications-privacy">Beim Öffnen werden deine aktuellen Rechte erneut geprüft. Private Inhalte erscheinen nicht in Vorschauen.</p>
    </div>
  );
}
